import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useTranslation } from '@/store/languageStore';
import {
  ArrowLeftIcon,
  PlusIcon,
  DocumentArrowUpIcon,
  PlayIcon,
  BeakerIcon,
  ServerStackIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  FolderIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import api from '@/services/api/client';
import toast from 'react-hot-toast';
import { TEST_IMPORT_ROW_LIMIT } from './types';
import type { UseImportsReturn } from './useImportsManagement';

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'success': return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
    case 'failed': return <XCircleIcon className="w-5 h-5 text-red-500" />;
    case 'partial': return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
    default: return <ClockIcon className="w-5 h-5 text-textTertiary" />;
  }
};

export const ImportsList = ({ state }: { state: UseImportsReturn }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    definitions, historyRuns, historyTotal, historyLoading,
    historyFilterDefinitionId, setHistoryFilterDefinitionId,
    historyFilterStatus, setHistoryFilterStatus,
    loadHistoryRuns, runImport, deleteDefinition, loadDefinitionForEdit,
    openFtpModal, openSourceFilesModal,
    runImportFromLatestSource, sourceLatestImportingDefinitionId,
    runImportPendingFromSource, sourcePendingImportingDefinitionId,
    resetWizard, setShowWizard,
    setDetailRunId, setDetailRun,
  } = state;

  return (
    <>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(ROUTES.SYSTEM_ADMINISTRATION)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
              <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
            </button>
            <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
              <ArrowUpTrayIcon className="w-6 h-6 text-primary" />
              {t('imports.pageTitle')}
            </h1>
          </div>
          <p className="text-textSecondary mt-1">
            {t('imports.pageDescription')}
          </p>
        </div>
        <button
          onClick={() => { resetWizard(); setShowWizard(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          data-testid="create-import-button"
        >
          <PlusIcon className="w-5 h-5" />
          {t('imports.createNew')}
        </button>
      </div>

      {/* Definitions list */}
      <div className="bg-bgElevated rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-bgBase">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-textSecondary">{t('imports.colName')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-textSecondary">{t('imports.colTargetTable')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-textSecondary">{t('imports.colSchedule')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-textSecondary">{t('imports.colLastRun')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-textSecondary">{t('imports.status')}</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-textSecondary">{t('imports.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {definitions.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-textSecondary">
                  <DocumentArrowUpIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t('imports.noImports')}</p>
                  <button
                    onClick={() => { resetWizard(); setShowWizard(true); }}
                    className="mt-3 text-primary hover:underline"
                  >
                    {t('imports.createFirstImport')}
                  </button>
                </td>
              </tr>
            ) : (
              definitions.map((def) => (
                <tr key={def.id} className="hover:bg-bgBase/50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-textDefault">{def.name}</div>
                    {def.description && (
                      <div className="text-sm text-textSecondary truncate max-w-xs">{def.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-textSecondary">{def.targetTable}</td>
                  <td className="px-4 py-3">
                    <span className="capitalize text-textSecondary">{def.scheduleType}</span>
                  </td>
                  <td className="px-4 py-3 text-textSecondary">
                    {def.lastRunAt ? new Date(def.lastRunAt).toLocaleString('sv-SE') : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(def.lastRunStatus)}
                      <span className="capitalize text-sm">{def.lastRunStatus}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => runImport(def.id)}
                        className="p-2 text-green-500 hover:bg-green-500/10 rounded-lg"
                        title={t('importsManagement.runImport')}
                        data-testid={`run-import-${def.id}`}
                      >
                        <PlayIcon className="w-5 h-5" />
                      </button>
                      {def.sourceId != null && (
                        <button
                          onClick={() => runImportFromLatestSource(def.id)}
                          disabled={sourceLatestImportingDefinitionId === def.id}
                          className="p-2 text-emerald-500 hover:bg-emerald-500/10 rounded-lg disabled:opacity-60"
                          title={t('imports.runLatestSource') || 'Tvinga körning från senaste källfil'}
                          data-testid={`run-latest-source-${def.id}`}
                        >
                          {sourceLatestImportingDefinitionId === def.id ? (
                            <ArrowPathIcon className="w-5 h-5 animate-spin" />
                          ) : (
                            <ArrowPathIcon className="w-5 h-5" />
                          )}
                        </button>
                      )}
                      {def.sourceId != null && (
                        <button
                          onClick={() => runImportPendingFromSource(def.id)}
                          disabled={sourcePendingImportingDefinitionId === def.id}
                          className="p-2 text-teal-500 hover:bg-teal-500/10 rounded-lg disabled:opacity-60"
                          title={t('imports.runPendingSourceTitle')}
                          data-testid={`run-pending-source-${def.id}`}
                        >
                          {sourcePendingImportingDefinitionId === def.id ? (
                            <ArrowPathIcon className="w-5 h-5 animate-spin" />
                          ) : (
                            <ArrowUpTrayIcon className="w-5 h-5" />
                          )}
                        </button>
                      )}
                      {def.sourceId != null && (
                        <button
                          onClick={() => openSourceFilesModal(def)}
                          className="p-2 text-primary hover:bg-primary/10 rounded-lg"
                          title={t('importsManagement.filesFromSource')}
                          data-testid={`source-files-${def.id}`}
                        >
                          <FolderIcon className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => openFtpModal(def.id)}
                        className="p-2 text-blue-500 hover:bg-blue-500/10 rounded-lg"
                        title={t('importsManagement.runImportFromFtp')}
                        data-testid={`run-ftp-import-${def.id}`}
                      >
                        <ServerStackIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => runImport(def.id, { dryRun: true, rowLimit: TEST_IMPORT_ROW_LIMIT })}
                        className="p-2 text-blue-500 hover:bg-blue-500/10 rounded-lg"
                        title={`Testimport (${TEST_IMPORT_ROW_LIMIT} rader)`}
                        data-testid={`run-test-import-${def.id}`}
                      >
                        <BeakerIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => loadDefinitionForEdit(def.id)}
                        className="p-2 text-textSecondary hover:bg-bgBase rounded-lg"
                        title={t('imports.edit')}
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => deleteDefinition(def.id)}
                        className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                        title={t('imports.delete')}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Import history */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-textDefault mb-4">{t('imports.importHistoryTitle')}</h2>
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div>
            <label className="block text-xs text-textSecondary mb-1">{t('imports.colDefinition')}</label>
            <select
              value={historyFilterDefinitionId}
              onChange={(e) => setHistoryFilterDefinitionId(e.target.value)}
              className="px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault text-sm min-w-[200px]"
            >
              <option value="">{t('imports.allDefinitions')}</option>
              {definitions.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-textSecondary mb-1">{t('imports.status')}</label>
            <select
              value={historyFilterStatus}
              onChange={(e) => setHistoryFilterStatus(e.target.value)}
              className="px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault text-sm"
            >
              <option value="">{t('imports.statusAll')}</option>
              <option value="success">{t('imports.statusSuccess')}</option>
              <option value="failed">{t('imports.statusFailed')}</option>
              <option value="partial">{t('imports.statusPartial')}</option>
              <option value="running">{t('imports.statusRunning')}</option>
              <option value="queued">{t('imports.statusQueued')}</option>
              <option value="cancelled">{t('imports.statusCancelled')}</option>
            </select>
          </div>
          <button
            type="button"
            onClick={() => loadHistoryRuns()}
            disabled={historyLoading}
            className="mt-5 px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault text-sm hover:bg-bgElevated disabled:opacity-50"
          >
            {historyLoading ? t('common.loading') : t('imports.historyUpdateBtn')}
          </button>
        </div>
        <div className="rounded-lg border border-border overflow-hidden bg-bgElevated">
          <table className="w-full text-sm">
            <thead className="bg-bgBase border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-textSecondary">{t('imports.colDefinition')}</th>
                <th className="px-4 py-3 text-left font-medium text-textSecondary">{t('imports.colFileSource')}</th>
                <th className="px-4 py-3 text-left font-medium text-textSecondary">{t('imports.status')}</th>
                <th className="px-4 py-3 text-left font-medium text-textSecondary">{t('imports.colStarted')}</th>
                <th className="px-4 py-3 text-left font-medium text-textSecondary">{t('imports.colRows')}</th>
                <th className="px-4 py-3 text-left font-medium text-textSecondary">{t('imports.colErrors')}</th>
                <th className="px-4 py-3 text-right font-medium text-textSecondary">{t('imports.colAction')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {historyLoading && historyRuns.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-textSecondary">{t('imports.historyLoadingText')}</td>
                </tr>
              ) : historyRuns.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-textSecondary">{t('imports.noRunsFound')}</td>
                </tr>
              ) : (
                historyRuns.map((run) => {
                  const firstErr = Array.isArray(run.errorLog) && run.errorLog.length > 0 ? run.errorLog[0] : null;
                  const errorText = firstErr && typeof firstErr === 'object' && 'message' in firstErr
                    ? (firstErr as { message: string }).message
                    : (typeof firstErr === 'string' ? firstErr : null);
                  return (
                    <tr key={run.id} className="hover:bg-bgBase/50">
                      <td className="px-4 py-3 text-textDefault">{run.definitionName ?? run.definitionId}</td>
                      <td className="px-4 py-3 text-textSecondary truncate max-w-[180px]" title={run.sourceFilename ?? '-'}>
                        {run.sourceFilename ?? '-'}
                      </td>
                      <td className="px-4 py-3"><span className="capitalize">{run.status}</span></td>
                      <td className="px-4 py-3 text-textSecondary">
                        {run.startedAt ? new Date(run.startedAt).toLocaleString('sv-SE') : '-'}
                      </td>
                      <td className="px-4 py-3 text-textSecondary">
                        {run.rowsTotal} totalt · {run.rowsImported} imp · {run.rowsUpdated} uppd · {run.rowsFailed} fel
                      </td>
                      <td className="px-4 py-3 text-textSecondary max-w-[220px] truncate" title={errorText ?? undefined}>
                        {errorText ?? (run.rowsFailed > 0 ? `${run.rowsFailed} fel` : '—')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={async () => {
                            setDetailRunId(run.id);
                            try {
                              const res = await api.get(`/api/admin/imports/runs/${run.id}`);
                              if (res.data.success) setDetailRun(res.data.data);
                            } catch {
                              toast.error(t('imports.loadRunError'));
                            }
                          }}
                          className="text-primary hover:underline text-sm"
                        >
                          {t('imports.showDetails')}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {historyTotal > 0 && (
          <p className="mt-2 text-sm text-textSecondary">
            {t('imports.showingRuns').replace('{shown}', String(historyRuns.length)).replace('{total}', String(historyTotal))}
          </p>
        )}
      </div>
    </>
  );
};
